/**
 * The booth station, the paper day: the day's lists, tomorrow, entering the paper sheet (split out of BoothStation.tsx
 * by interaction — James, Oct 21, item 5; no behavior change).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatCountDay, formatTime } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import { useAdminView } from './BoothAdmin.js';
import { type Found, hours, stat } from './BoothShared.js';

export function BoothLists({ children }: { children?: ReactNode }): ReactNode {
  const { v, said } = useAdminView();
  return (
    <div className="booth__found" data-testid="booth-lists">
      {said ? <p className="booth__refusal">{said}</p> : null}
      {v ? (
        <>
          <section className="booth__card booth__card--red">
            <div className="booth__cardhead">
              NO-SHOWS
              <span className="booth__cardsub">
                {String(v.noShowList?.length ?? 0)} · shift over, never arrived
              </span>
            </div>
            {(v.noShowList ?? []).length === 0 ? (
              <div className="booth__row">
                <span className="booth__rowc">Nobody yet.</span>
              </div>
            ) : null}
            {(v.noShowList ?? []).map((x) => (
              <div className="booth__row" key={`${x.personId}${x.startsAt}`}>
                <span className="booth__rowa">
                  <b>{x.name}</b>
                  <span className="booth__rowb">
                    {x.area} · {hours(x.startsAt, x.endsAt)}
                  </span>
                </span>
                <span className="booth__chip booth__chip--bad">NO-SHOW</span>
              </div>
            ))}
            <div className="booth__cardfoot">
              A no-show is marked at shift end, not before; a late person is late.
            </div>
          </section>
          {children}
          <section className="booth__card booth__card--gold">
            <div className="booth__cardhead">
              UNSIGNED WAIVERS AMONG TODAY’S SHIFTS
              <span className="booth__cardsub">
                {v.unsigned === null
                  ? 'no waiver is set for this year'
                  : `${String(v.unsigned.length)} · they sign at the booth`}
              </span>
            </div>
            {v.unsigned?.length === 0 ? (
              <div className="booth__row">
                <span className="booth__rowc">Everyone on today’s shifts has signed.</span>
              </div>
            ) : null}
            {v.unsigned?.slice(0, 12).map((x) => (
              <div className="booth__row" key={x.personId}>
                <span className="booth__rowa">
                  <b>{x.name}</b>
                  <span className="booth__rowb">
                    {x.area} · {formatTime(x.startsAt)}
                  </span>
                </span>
                {x.parent ? (
                  <>
                    <span className="booth__rowc">
                      Needs a parent — {x.parent.name} has not signed ahead on the family page.
                    </span>
                    <span className="booth__chip booth__chip--bad">PARENT</span>
                  </>
                ) : (
                  <span className="booth__chip booth__chip--warn">AT ARRIVAL</span>
                )}
              </div>
            ))}
            {v.unsigned && v.unsigned.length > 12 ? (
              <div className="booth__row">
                <span className="booth__rowa">
                  <b>{String(v.unsigned.length - 12)} more</b>
                </span>
              </div>
            ) : null}
          </section>
          <section className="booth__card">
            <div className="booth__cardhead">
              MINORS ON SITE
              <span className="booth__cardsub">
                {String(v.minorsHere?.length ?? 0)} · checked in now
              </span>
            </div>
            {(v.minorsHere ?? []).length === 0 ? (
              <div className="booth__row">
                <span className="booth__rowc">No minors checked in.</span>
              </div>
            ) : null}
            {(v.minorsHere ?? []).map((x) => (
              <div className="booth__row" key={`${x.personId}${x.inAt}`}>
                <span className="booth__rowa">
                  <b>
                    {x.name}
                    {x.age !== null ? `, ${String(x.age)}` : ''}
                  </b>
                  <span className="booth__rowb">
                    {x.area} · in {formatTime(x.inAt)}
                  </span>
                </span>
                <span className="booth__chip booth__chip--ok">ON SITE</span>
              </div>
            ))}
          </section>
        </>
      ) : null}
    </div>
  );
}

/**
 * **Tomorrow** (`Booth.dc.html` tomorrow, Rebuild 29 state 30): the next festival day, prepared
 * tonight — expected, the rush, unsigned, groups, minors, and the arrivals by hour.
 */
export function BoothTomorrow(): ReactNode {
  const { v, said } = useAdminView();
  const tm = v?.tomorrow ?? null;
  return (
    <div className="booth__found" data-testid="booth-tomorrow">
      {said ? <p className="booth__refusal">{said}</p> : null}
      {v ? (
        tm ? (
          <>
            <div className="booth__strip">
              {stat(String(tm.expected), 'EXPECTED')}
              {tm.rush
                ? stat(String(tm.rush.n), `ARRIVE AT ${formatTime(tm.rush.at)} · THE RUSH`, 'warn')
                : null}
              {stat(
                tm.unsigned === null ? '—' : String(tm.unsigned),
                'UNSIGNED · WAIVERS EXPECTED',
                tm.unsigned ? 'warn' : undefined,
              )}
              {stat(
                String(tm.groups.length),
                tm.groups.length
                  ? `GROUPS · ${tm.groups.map((g) => `${g.name} ${String(g.n)}`).join(' · ')}`
                  : 'GROUPS',
              )}
              {stat(String(tm.minors), 'MINORS')}
            </div>
            <section className="booth__card">
              <div className="booth__cardhead">
                ARRIVING
                <span className="booth__cardsub">{formatCountDay(tm.day)} · by hour</span>
              </div>
              {tm.byHour.map((h) => (
                <div className="booth__row" key={h.at}>
                  <span className="booth__rowa">
                    <b>{formatTime(h.at)}</b>
                  </span>
                  <span className="booth__rowc">{String(h.n)} arrive</span>
                </div>
              ))}
            </section>
          </>
        ) : (
          <p className="booth__fine">This is the festival’s last day.</p>
        )
      ) : null}
    </div>
  );
}

/**
 * **Enter the paper** (`Booth.dc.html` paper, Rebuild 29 state 32; ON11 item 3): a station's paper
 * sheet, line by line as written — the sheet's shift, then each line (the name, the time in, out
 * if written, whether they signed the paper waiver). Each line is matched against People as it is
 * typed: **matched** (Enter), **pick one** (two people answer to it), **check** (two people on one
 * line — Split), or **no match**. **Enter all matched** takes the rest. Each is marked "from paper ·
 * entered by" the desk.
 */
export interface PaperLine {
  key: number;
  name: string;
  inTime: string;
  outTime: string;
  waiver: boolean;
  /** Who it matched; several when the line needs a pick. */
  matches: Found[] | null;
  picked: Found | null;
  entered: string | null;
}
export function EnterThePaper({ desk }: { desk: string | null }): ReactNode {
  const [shifts, setShifts] = useState<
    { shiftId: string; area: string; day: string; startsAt: string; endsAt: string }[]
  >([]);
  const [shiftId, setShiftId] = useState('');
  const [lines, setLines] = useState<PaperLine[]>([]);
  const [name, setName] = useState('');
  const [inTime, setInTime] = useState('');
  const [outTime, setOutTime] = useState('');
  const [waiver, setWaiver] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nextKey = useRef(1);
  useEffect(() => {
    api<{ rows: typeof shifts }>('GET', '/volunteers/booth/paper-shifts')
      .then((r) => {
        setShifts(r.rows);
      })
      .catch(() => undefined);
  }, []);
  const sheet = shifts.find((x) => x.shiftId === shiftId) ?? null;
  const match = (line: PaperLine): void => {
    api<{ rows: Found[] }>('GET', `/volunteers/booth/find?q=${encodeURIComponent(line.name)}`)
      .then((r) => {
        setLines((ls) =>
          ls.map((l) =>
            l.key === line.key
              ? { ...l, matches: r.rows, picked: r.rows.length === 1 ? (r.rows[0] ?? null) : null }
              : l,
          ),
        );
      })
      .catch(() => undefined);
  };
  const add = (n: string): void => {
    const line: PaperLine = {
      key: nextKey.current++,
      name: n.trim(),
      inTime,
      outTime,
      waiver,
      matches: null,
      picked: null,
      entered: null,
    };
    setLines((ls) => [...ls, line]);
    if (!/\band\b|&/i.test(line.name)) match(line);
  };
  const enter = async (l: PaperLine): Promise<void> => {
    if (!l.picked || !shiftId) return;
    try {
      // The times as written on the sheet; the API puts them on the shift's own day.
      const r = await api<{ says: string }>('POST', '/volunteers/booth/paper', {
        personId: l.picked.personId,
        shiftId,
        inTime: l.inTime,
        outTime: l.outTime || null,
        waiverSigned: l.waiver,
      });
      setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, entered: r.says } : x)));
    } catch (e) {
      setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, entered: sentence(e) } : x)));
    }
  };
  const ready = lines.filter((l) => l.picked && !l.entered);
  return (
    <div className="booth__found" data-testid="booth-paper">
      {!desk ? (
        <p className="booth__refusal">
          Sign in at the desk first — the paper is entered in your name.
        </p>
      ) : null}
      <section className="booth__card">
        <div className="booth__cardhead">
          {sheet
            ? `${formatCountDay(sheet.day).toUpperCase()} SHEET · ${sheet.area.toUpperCase()} · ${hours(sheet.startsAt, sheet.endsAt)}`
            : 'THE SHEET'}
          <span className="booth__cardsub">
            {String(lines.length)} {lines.length === 1 ? 'line' : 'lines'}
            {desk ? ` · ENTER THE PAPER · AS ${desk.toUpperCase()}` : ''}
          </span>
        </div>
        <label className="booth__row">
          <span className="booth__k">SHIFT</span>
          <select
            className="booth__input"
            aria-label="Shift on the sheet"
            value={shiftId}
            onChange={(e) => {
              setShiftId(e.target.value);
            }}
          >
            <option value="">Pick the shift</option>
            {shifts.map((x) => (
              <option key={x.shiftId} value={x.shiftId}>
                {formatCountDay(x.day)} · {x.area} · {hours(x.startsAt, x.endsAt)}
              </option>
            ))}
          </select>
        </label>
        {lines.map((l) => {
          const two = /\band\b|&/i.test(l.name) && !l.matches;
          const many = (l.matches?.length ?? 0) > 1 && !l.picked;
          const none = l.matches?.length === 0;
          const chip: readonly [string, string] = l.entered
            ? ['ENTERED', 'ok']
            : two
              ? ['CHECK', 'warn']
              : many
                ? ['PICK', 'warn']
                : none
                  ? ['NO MATCH', 'bad']
                  : l.picked
                    ? ['MATCHED', 'ok']
                    : ['…', 'in'];
          return (
            <div
              className={`booth__row${chip[1] === 'warn' ? ' booth__row--warn' : ''}`}
              key={l.key}
            >
              <span className="booth__rowa">
                <b>{l.picked ? l.picked.name : `“${l.name}”`}</b>
                <span className="booth__rowb">
                  {l.inTime ? `signed ${l.inTime}` : 'no time written'}
                  {l.outTime ? ` – ${l.outTime}` : ''}
                  {l.waiver ? ' · paper waiver' : ''}
                </span>
              </span>
              <span className="booth__rowc">
                {l.entered ??
                  (two
                    ? 'Two people on one line.'
                    : many
                      ? `${String(l.matches?.length ?? 0)} people answer to that name.`
                      : none
                        ? 'Nobody in People by that name — a walk-up at the station.'
                        : l.picked
                          ? 'Found in People.'
                          : 'Matching…')}
              </span>
              <span className={`booth__chip booth__chip--${chip[1]}`}>{chip[0]}</span>
              {two ? (
                <button
                  type="button"
                  className="booth__act"
                  onClick={() => {
                    // "Tom and Jen Rusinski": two lines, the family name on each.
                    const parts = l.name.split(/\s+(?:and|&)\s+/i);
                    const family = (parts[parts.length - 1] ?? '').split(/\s+/).slice(1).join(' ');
                    setLines((ls) => ls.filter((x) => x.key !== l.key));
                    for (const part of parts)
                      add(part.includes(' ') || !family ? part : `${part} ${family}`);
                  }}
                >
                  Split
                </button>
              ) : null}
              {many
                ? (l.matches ?? []).slice(0, 4).map((m) => (
                    <button
                      type="button"
                      className="booth__act"
                      key={m.personId}
                      onClick={() => {
                        setLines((ls) =>
                          ls.map((x) => (x.key === l.key ? { ...x, picked: m } : x)),
                        );
                      }}
                    >
                      {m.name}
                    </button>
                  ))
                : null}
              {l.picked && !l.entered ? (
                <button
                  type="button"
                  className="booth__act"
                  disabled={busy || !desk || !shiftId}
                  onClick={() => {
                    void enter(l);
                  }}
                >
                  Enter
                </button>
              ) : null}
            </div>
          );
        })}
        <div className="booth__row">
          <span className="booth__rowa">
            <b>A line from the sheet</b>
          </span>
          <input
            className="booth__input"
            aria-label="Name on the sheet"
            autoComplete="off"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <input
            className="booth__input"
            aria-label="Time in on the sheet"
            type="time"
            value={inTime}
            onChange={(e) => {
              setInTime(e.target.value);
            }}
          />
          <input
            className="booth__input"
            aria-label="Time out on the sheet"
            type="time"
            value={outTime}
            onChange={(e) => {
              setOutTime(e.target.value);
            }}
          />
          <label>
            <input
              type="checkbox"
              checked={waiver}
              onChange={(e) => {
                setWaiver(e.target.checked);
              }}
            />{' '}
            Signed the paper waiver
          </label>
          <button
            type="button"
            className="booth__act"
            disabled={!name.trim() || !inTime}
            onClick={() => {
              add(name);
              setName('');
              setInTime('');
              setOutTime('');
              setWaiver(false);
            }}
          >
            Add the line
          </button>
        </div>
        <div className="booth__cardfoot">
          Each is marked “from paper · entered by {desk ?? 'the desk'}”. A paper waiver reads as
          that year’s waiver on the person.
        </div>
      </section>
      {said ? (
        <p className="booth__fine" role="status">
          {said}
        </p>
      ) : null}
      <div className="booth__bigrow">
        <button
          type="button"
          className="booth__big"
          data-testid="booth-big"
          disabled={busy || !desk || !shiftId || ready.length === 0}
          onClick={() => {
            setBusy(true);
            void (async () => {
              for (const l of ready) await enter(l);
              setSaid(`${String(ready.length)} entered from the paper.`);
              setBusy(false);
            })();
          }}
        >
          {ready.length ? `Enter all ${String(ready.length)} matched` : 'Nothing matched yet'}
        </button>
      </div>
    </div>
  );
}
